import { LocalFind, LocalInsert } from '@weblite/persist'
import { v4 as uuidv4 } from 'uuid'
import { envMode, envModes } from '$config/env'
import { bufferFunction } from '$func/buffer-function'
import { getClientUserIdentityToken, isLoggedIn } from '$logic/auth'
import { getCurrentRelease } from '$logic/client-device/storage'
import { Client, Event } from '$logic/analytics/types'
import { getProfile } from '$logic/profile'
import { retryUntilSucceed } from '$func/retryFunction'
import { postRequest } from './api'
import { twaVersion } from '$func/environmentInfo'

const ANALYTICS_BUFFER_TIME = 1000
const DEVICE_ID = 'deviceId'
const deviceId = LocalFind(DEVICE_ID) || LocalInsert(DEVICE_ID, uuidv4())

const SESSION_NUMBER = 'sessionNumber'
const sessionNumber = LocalInsert(
  SESSION_NUMBER,
  (LocalFind(SESSION_NUMBER) || 0) + 1,
)

const SESSION_START_TIME = 'sessionStartTime'
const sessionStartTime =
  LocalFind(SESSION_START_TIME) || LocalInsert(SESSION_START_TIME, new Date())

let sessionId = ''
let tempSessionId = ''

export async function authorizeAnalyticsBeforeLogin() {
  const { version } = getCurrentRelease()
  const client: Client = {
    dId: deviceId,
    sNum: sessionNumber,
    sTime: sessionStartTime,
    appVer: version,
    ...(twaVersion && { twaVersion }),
  }
  const res = await postRequest<string>('', {
    user: {},
    client,
  })
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  tempSessionId = res.data.sessionId
}

export async function authorizeAnalyticsAfterLogin() {
  const userJWT = await getClientUserIdentityToken()
  const { gender } = getProfile()
  const user = { gender }
  const { version } = getCurrentRelease()
  const client: Client = {
    dId: deviceId,
    sNum: sessionNumber,
    sTime: sessionStartTime,
    appVer: version,
    ...(twaVersion && { twaVersion }),
  }

  const res = await postRequest<string>(
    ``,
    {
      user,
      client,
    },
    { headers: { authorization: `Bearer ${userJWT}` } },
  )
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line prefer-destructuring
  sessionId = res.data.sessionId
}

export const sendAnalyticsRequest = async (events: Event[]) => {
  const isUserLoggedIn = isLoggedIn()
  await postRequest<string>(
    `${isUserLoggedIn ? sessionId : tempSessionId}/events`,
    {
      events,
    },
  ).catch(error => {
    if (error.response.data.message === 'SESSION_NOT_FOUND' && isUserLoggedIn) {
      sessionId = ''
      authorizeAnalyticsAfterLogin().then(() => sendAnalyticsRequest(events))
    }
  })
}

export const sendAnalyticsWithRetry = async (input: {
  type: Event['type']
  payload?: Event['payload']
  config?: { times: number; interval: number }
}) => {
  const isUserLoggedIn = isLoggedIn()
  if (isUserLoggedIn && !sessionId) await authorizeAnalyticsAfterLogin()
  if (!isUserLoggedIn && !tempSessionId) await authorizeAnalyticsBeforeLogin()
  retryUntilSucceed(
    () =>
      sendAnalyticsRequest([
        { type: input.type, payload: input.payload, ts: new Date() },
      ]),
    { times: 3, interval: 1 },
  )
}

export const sendAnalytics = bufferFunction(async (events: Event[]) => {
  if (envMode !== envModes.PRODUCTION) return
  const isUserLoggedIn = isLoggedIn()

  if (isUserLoggedIn && !sessionId) await authorizeAnalyticsAfterLogin() // TODO: Technical debt : We have race here
  if (!isUserLoggedIn && !tempSessionId) await authorizeAnalyticsBeforeLogin() // TODO: Technical debt : We have race here
  sendAnalyticsRequest(events)
}, ANALYTICS_BUFFER_TIME)

export async function migrateFromTempSessionIdToSessionId() {
  const userJWT = await getClientUserIdentityToken()
  if (!tempSessionId) return
  await postRequest<string>(`${tempSessionId}/login`, null, {
    headers: {
      authorization: `Bearer ${userJWT}`,
    },
  })
  tempSessionId = ''
}
